/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { Typography, Box, Grid, useTheme } from '@mui/material';
import moment from 'moment';
import modifyScriptName from '../../../../utils/modifyScriptName';

const TradeHistoryRow = ({ trade }) => {
  const theme = useTheme();
  const { type, buy, sell, buyBrokerage, sellBrokerage, boughtAt, soldAt, script, profit, side, updatedAt } = trade;

  const formatDateTime = (date) => {
    return moment(date).format('DD MMM hh:mm:ss A');
  };

  const formatIndianCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(value);
  };

  let color;
  if (['EXECUTED', 'CANCELLED'].includes(type)) {
    color = type === 'EXECUTED' ? theme.palette.success.main : theme.palette.error.main;
  } else if (type === 'CLOSE') {
    color = side === 'BUY' ? theme.palette.success.main : theme.palette.error.main;
  } else if (type === 'ORDER') {
    color = side === 'BUY' ? theme.palette.error.main : theme.palette.success.main;
  } else {
    color = side === 'BUY' ? theme.palette.success.main : theme.palette.error.main;
  }

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${color}`,
        width: '100%',
        py: 1,
        px: 1.5,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
          transform: 'translateX(2px)',
        },
        boxShadow: `0 1px 2px 0 ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.2)'}`,
        borderRadius: '4px',
        mb: 0.75,
        bgcolor: theme.palette.mode === 'light' ? 'background.paper' : 'background.neutral',
      }}
    >
      <Grid container spacing={1} alignItems="center">
        {/* Symbol and Profit */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  textTransform: 'uppercase',
                }}
              >
                {modifyScriptName(script.tradingsymbol, script.expiry)}
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color,
                bgcolor: theme.palette.mode === 'light' ? `${color}08` : `${color}15`,
                px: 1,
                py: 0.25,
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}
              >
                {trade.type === 'ORDER' && `${side === 'BUY' ? 'SELL' : 'BUY'}`}
                {trade.type === 'CLOSE' && `${side}`}
                {trade.type === 'OPEN' && `${side}`}
                {['EXECUTED', 'CANCELLED'].includes(trade.type) && `${type} ${side}`}
                {' • '}
                {trade.type === 'ORDER'
                  ? trade.quantity
                  : trade.type === 'OPEN' || trade.type === 'CLOSE'
                    ? trade.side === 'BUY'
                      ? trade.buyQuantity
                      : trade.sellQuantity
                    : trade.quantity}
              </Typography>
            </Box>
            {profit !== undefined && (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: profit > 0 ? theme.palette.success.main : theme.palette.error.main,
                }}
              >
                {formatIndianCurrency(profit)}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Price Info and Dates in Single Row */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Prices Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {buy && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  BUY: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{buy?.toFixed(2)}</Box>
                </Typography>
              )}
              {sell && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  SELL: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{sell?.toFixed(2)}</Box>
                </Typography>
              )}
              {type === 'ORDER' && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  BRK: <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    {(+buyBrokerage?.toFixed(2)) + (+sellBrokerage?.toFixed(2))}
                  </Box>
                </Typography>
              )}
              {(type === 'CLOSE' || type === 'OPEN') && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  BRK: <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    {side === 'BUY' ? buyBrokerage?.toFixed(2) : sellBrokerage?.toFixed(2)}
                  </Box>
                </Typography>
              )}
            </Box>
            {/* Dates Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {type === 'ORDER' && (
                <Grid container columnGap={2.5}>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>BOUGHT AT: </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>SOLD AT:</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatDateTime(boughtAt)}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatDateTime(soldAt)}</Typography>
                  </Grid>
                </Grid>
              )}

              {['EXECUTED', 'CANCELLED'].includes(type) && (
                <Grid container columnGap={2.5}>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {type === 'CANCELLED' ? 'CANCELLED AT' : 'UPDATED AT'}:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {formatDateTime(updatedAt)}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {(type === 'CLOSE' || type === 'OPEN') && (
                <Grid container columnGap={2.5}>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {side === 'BUY' ? 'BOUGHT AT' : 'SOLD AT'}:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {formatDateTime(side === 'BUY' ? boughtAt : soldAt)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TradeHistoryRow;
